import { MaskStylesObj } from '@reactour/mask';
import { PopoverStylesObj } from '@reactour/popover';
import { StylesObj } from '@reactour/tour';
import {
	ArrayComparisonConfig,
	CustomLogConfig,
	MatchActions,
	MicroTaskConfigType,
} from 'ihub/common';

import {
	getIfRuleTourSteps,
	getTaskDestinationTourSteps,
	getTourCompletedStep,
} from './component-tours';
import { AdvancedTourDefinition, MicroTaskTourId } from './shared-types';
import { getAddItemTaskTourSteps } from './task-tours/add-item-task-tour-steps';
import { getArrayComparisonTourSteps } from './task-tours/array-comparison-tour-steps';
import { getArrayToRecordsTourSteps } from './task-tours/array-to-records-tour-steps';
import { getBatchingTourSteps } from './task-tours/batching-tour-steps';
import { getClearDataTourSteps } from './task-tours/clear-data-tour-steps';
import { getCustomLogTourSteps } from './task-tours/custom-log-tour-steps';
import { getDataMapperTourSteps } from './task-tours/data-mapper-tour-steps';
import { getDatabaseConnectorTourSteps } from './task-tours/database-connector-tour-steps';
import { getDelayTourSteps } from './task-tours/delay-tour-steps';
import { getDuplicateCheckTourSteps } from './task-tours/duplicate-check-tour-steps';
import { getExecuteFlowTourSteps } from './task-tours/execute-flow-tour-steps';
import { getGetVariableTourSteps } from './task-tours/get-variable-tour-steps';
import { getGoogleSheetsQueryTourSteps } from './task-tours/google-sheets-query-tour-steps';
import { getHubspotDeleteObjectTourSteps } from './task-tours/hubspot-delete-object-tour-steps';
import { getHubspotGetObjectsTourSteps } from './task-tours/hubspot-get-objects-tour-steps';
import { getHubspotSaveAssociationTourSteps } from './task-tours/hubspot-save-association-tour-steps';
import { getHubspotSaveObjectsTourSteps } from './task-tours/hubspot-save-objects-tour-steps';
import { getHubspotWebhookTourSteps } from './task-tours/hubspot-webhook-tour-steps';
import { getIfGateTourSteps } from './task-tours/if-gate-tour-steps';
import { getLoopTourSteps } from './task-tours/loop-tour-steps';
import { getMergeDataTourSteps } from './task-tours/merge-data-tour-steps';
import { getNotificationTourSteps } from './task-tours/notification-tour-steps';
import { getParallelJoinTourSteps } from './task-tours/parallel-join-tour-steps';
import { getParallelSplitTourSteps } from './task-tours/parallel-split-tour-steps';
import { getRateLimiterTourSteps } from './task-tours/rate-limiter-tour-steps';
import { getRestApiReceiverTourSteps } from './task-tours/rest-api-receiver-tour-steps';
import { getRestApiSenderTourSteps } from './task-tours/rest-api-sender-tour-steps';
import { getS3ReaderTourSteps } from './task-tours/s3-reader-tour-steps';
import { getS3WriterTourSteps } from './task-tours/s3-writer-tour-steps';
import { getSalesforceGetObjectsTourSteps } from './task-tours/salesforce-get-object-tour-steps';
import { getSalesforceSaveObjectsTourSteps } from './task-tours/salesforce-save-objects-tour-steps';
import { getScheduleTaskTourSteps } from './task-tours/schedule-task-tour-steps';
import { getScriptBlockTourSteps } from './task-tours/script-block-tour-steps';
import { getSetVariableTourSteps } from './task-tours/set-variable-tour-steps';
import { getSFTPReaderTourSteps } from './task-tours/sftp-reader-tour-steps';
import { getSFTPWriterTourSteps } from './task-tours/sftp-writer-tour-steps';
import { getTakeFromArrayTourSteps } from './task-tours/take-from-array-tour-steps';
import { getValidationTaskTourSteps } from './task-tours/validation-task-tour-steps';
import { getXeroCreateInvoiceTaskTourSteps } from './task-tours/xero-create-invoice-task-tour-steps';

export default function getTaskTourRegistry({
	defaultStyles,
}: {
	defaultStyles: (StylesObj & PopoverStylesObj & MaskStylesObj) | undefined;
}): Record<MicroTaskTourId, AdvancedTourDefinition> {
	const getTaskDefinition = (content: string) => {
		return {
			id: 'task-definition',
			selector: '[data-tour="task-definition"]',
			content: content,
			styles: defaultStyles,
		};
	};

	const destinations = getTaskDestinationTourSteps({ defaultStyles });
	const componentTourCompleted = getTourCompletedStep({
		defaultStyles,
		selector: 'task-definition',
		completionId: 'task-definition-completed',
	});
	const ifRuleSteps = getIfRuleTourSteps({ defaultStyles });

	const MicroTaskRegistry: Record<MicroTaskTourId, AdvancedTourDefinition> = {
		setVariableTask: {
			start: 'task-definition',
			steps: [
				getTaskDefinition(
					'The set variable task allows you to store values in variables that can be referenced and reused later in the flow.'
				),
				...destinations,
				...getSetVariableTourSteps({ defaultStyles }),
				componentTourCompleted,
			],
			autoStartNext: false,
		},
		arrayComparison: {
			start: 'task-definition',
			steps: (runtime?: { config?: MicroTaskConfigType | undefined }) => {
				if (!runtime?.config) return [];
				const config = runtime.config as ArrayComparisonConfig;

				const hasValidIfSteps = () => {
					if (
						config.collectionField1.length > 0 &&
						config.collectionField2.length > 0 &&
						config.matchAction !== MatchActions.CombineMatchingIndexes
					) {
						return true;
					}
					return false;
				};

				return [
					getTaskDefinition(
						'The array comparison task enables the user to compare two collections of data based on a set of rules and then to decide what part of the data is needed based on a configuration options. Note: There will only be "collection" (arrays) type fields in the dropdown options. Also, the two fields must be within the same record and not split between two records.'
					),
					...destinations,
					...getArrayComparisonTourSteps({ defaultStyles }),
					...(hasValidIfSteps() ? ifRuleSteps : []),
					componentTourCompleted,
				];
			},
			autoStartNext: false,
		},
		arrayToRecords: {
			start: 'task-definition',
			steps: [
				getTaskDefinition(
					'The array to records task is used to split array elements into separate records for processing.'
				),
				...destinations,
				...getArrayToRecordsTourSteps({ defaultStyles }),
				componentTourCompleted,
			],
			autoStartNext: false,
		},
		batching: {
			start: 'task-definition',
			steps: (runtime?: { config?: MicroTaskConfigType | undefined }) => [
				getTaskDefinition(
					'The batching task converts the incoming data into fixed size record batches.'
				),
				...destinations,
				...getBatchingTourSteps({ defaultStyles, microTaskConfigType: runtime?.config }),
				componentTourCompleted,
			],
			autoStartNext: false,
		},
		clearData: {
			start: 'task-definition',
			steps: [
				getTaskDefinition(
					'The Clear data task will take any incoming Processing Data and replace it with an empty object. Addtionally, it also has the functionality to replace the flow variables or record variables (or both) with an empty object.'
				),
				...destinations,
				...getClearDataTourSteps({ defaultStyles }),
				componentTourCompleted,
			],
			autoStartNext: false,
		},
		customLog: {
			start: 'task-definition',
			steps: (runtime?: { config?: MicroTaskConfigType | undefined }) => {
				if (!runtime?.config) return [];
				const config = runtime.config as CustomLogConfig;
				const hasValidIfSteps = () => {
					if (config.messageHandlers.length > 0) return true;

					return false;
				};
				return [
					getTaskDefinition(
						'The Custom Log task is used to log error, warning, info or debug messages, and intercept error messages from previous tasks and handle them based on a set of rules.'
					),
					...destinations,
					...getCustomLogTourSteps({ defaultStyles, microTaskConfigType: runtime?.config }),
					...(hasValidIfSteps() ? ifRuleSteps : []),
					componentTourCompleted,
				];
			},
			autoStartNext: false,
		},
		databaseConnector: {
			start: 'task-definition',
			steps: (runtime?: { config?: MicroTaskConfigType | undefined }) => [
				getTaskDefinition(
					'The Database Connector enables the ability to connect to a database and then run a query on the connected DB.'
				),
				...destinations,
				...getDatabaseConnectorTourSteps({ defaultStyles, microTaskConfigType: runtime?.config }),
				componentTourCompleted,
			],
			autoStartNext: false,
		},
		delay: {
			start: 'task-definition',
			steps: [
				getTaskDefinition(
					'The Delay task can be used to create a controlled and intentional delay in a flow. This is usually used when there is a need to slow down the flow or when a wait time needs to be added between network requests.'
				),
				...destinations,
				...getDelayTourSteps({ defaultStyles }),
				componentTourCompleted,
			],
			autoStartNext: false,
		},
		duplicateCheck: {
			start: 'task-definition',
			autoStartNext: false,
			steps: (runtime?: { config?: MicroTaskConfigType | undefined }) => [
				getTaskDefinition(
					'The Duplicate Check task enables the user to verfiy whether there is already an existing entry for the given item based on a "Unique" constraint.'
				),
				...destinations,
				...getDuplicateCheckTourSteps({
					defaultStyles,
					microTaskConfigType: runtime?.config,
				}),
				componentTourCompleted,
			],
		},
		executeFlow: {
			start: 'task-definition',
			steps: [
				getTaskDefinition(
					'The Execute Flow allows the user to use a seperate flow within the current flow, represented by a single task.'
				),
				...getExecuteFlowTourSteps({ defaultStyles }),
				componentTourCompleted,
			],
			autoStartNext: false,
		},
		getVariable: {
			start: 'task-definition',
			steps: (runtime?: { config?: MicroTaskConfigType | undefined }) => [
				getTaskDefinition(
					'The Get Variable task is used to retrieve the values of the configured variables during execution.'
				),
				...destinations,
				...getGetVariableTourSteps({ defaultStyles, microTaskConfigType: runtime?.config }),
				componentTourCompleted,
			],
			autoStartNext: false,
		},
		googleSheets: {
			start: 'task-definition',
			steps: [
				getTaskDefinition(
					'The Google Sheets Query task adds the ability to fetch data from a Google Sheets document.'
				),
				...destinations,
				...getGoogleSheetsQueryTourSteps({ defaultStyles }),
				componentTourCompleted,
			],
			autoStartNext: false,
		},
		hubspotDeleteObject: {
			start: 'task-definition',
			steps: (runtime?: { config?: MicroTaskConfigType | undefined }) => [
				getTaskDefinition(
					'The HubSpot Delete Object tasks enables the user to delete HubSpot objects.'
				),
				...destinations,
				...getHubspotDeleteObjectTourSteps({ defaultStyles, microTaskConfigType: runtime?.config }),
				componentTourCompleted,
			],
			autoStartNext: false,
		},
		loop: {
			start: 'task-definition',
			steps: (runtime?: { config?: MicroTaskConfigType | undefined }) => [
				getTaskDefinition('The loop task processes through the collection of items one at a time.'),
				...destinations,
				...getLoopTourSteps({ defaultStyles, microTaskConfigType: runtime?.config }),
				componentTourCompleted,
			],
			autoStartNext: false,
		},
		parallelJoin: {
			start: 'task-definition',
			steps: [
				getTaskDefinition(
					'The Parallel Join task is used to join two or more paths in a flow into a single path.'
				),
				...destinations,
				...getParallelJoinTourSteps({ defaultStyles }),
				componentTourCompleted,
			],
			autoStartNext: false,
		},
		parallelSplit: {
			start: 'task-definition',
			steps: (runtime?: { config?: MicroTaskConfigType | undefined }) => [
				getTaskDefinition(
					'The task takes the input data "as is" and duplicates it for each of the configured destination tasks. Then all of the configured destinations are kicked off in parallel.'
				),
				...destinations,
				...getParallelSplitTourSteps({ defaultStyles, microTaskConfigType: runtime?.config }),
				componentTourCompleted,
			],
			autoStartNext: false,
		},
		rateLimit: {
			start: 'task-definition',
			steps: [
				getTaskDefinition(
					'This task serves the purpose of limiting the amount of executions that get processed through a flow.'
				),
				...destinations,
				...getRateLimiterTourSteps({ defaultStyles }),
				componentTourCompleted,
			],
			autoStartNext: false,
		},
		restApiReceiver: {
			start: 'task-definition',
			steps: [
				getTaskDefinition(
					'The REST API Receiver service exposes a REST API on the public internet for other REST clients to communicate with iHub.'
				),
				...destinations,
				...getRestApiReceiverTourSteps({ defaultStyles }),
				componentTourCompleted,
			],
			autoStartNext: false,
		},
		s3Reader: {
			start: 'task-definition',
			steps: (runtime?: { config?: MicroTaskConfigType | undefined }) => [
				getTaskDefinition('The S3 Reader task enables iHub to read files from an AWS S3 bucket.'),
				...destinations,
				...getS3ReaderTourSteps({ defaultStyles, microTaskConfigType: runtime?.config }),
				componentTourCompleted,
			],
			autoStartNext: false,
		},
		s3Writer: {
			start: 'task-definition',
			steps: [
				getTaskDefinition(
					'The S3 Writer provides iHub the ability to write files to an AWS S3 Bucket.'
				),
				...destinations,
				...getS3WriterTourSteps({ defaultStyles }),
				componentTourCompleted,
			],
			autoStartNext: false,
		},
		salesforceGetObject: {
			start: 'task-definition',
			steps: (runtime?: { config?: MicroTaskConfigType | undefined }) => [
				getTaskDefinition(
					'The Salesforce Get Objects task allows the user to fetch one or more data records of the specified object type.'
				),
				...destinations,
				...getSalesforceGetObjectsTourSteps({
					defaultStyles,
					microTaskConfigType: runtime?.config,
				}),
				componentTourCompleted,
			],
			autoStartNext: false,
		},
		dataMapper: {
			start: 'task-definition',
			steps: (runtime?: { config?: MicroTaskConfigType | undefined }) => [
				getTaskDefinition(
					'The Data mapper is used to "Map" or Route data from one Payload to another using a mapping rule.'
				),
				...destinations,
				...getDataMapperTourSteps({ defaultStyles, microTaskConfigType: runtime?.config }),
				componentTourCompleted,
			],
			autoStartNext: false,
		},
		hubspotGetObjects: {
			start: 'task-definition',
			steps: (runtime?: { config?: MicroTaskConfigType | undefined }) => [
				getTaskDefinition(
					'The HubSpot Get Objects task enables the user to retrieve a specific object(s) from HubSpot.'
				),
				...destinations,
				...getHubspotGetObjectsTourSteps({ defaultStyles, microTaskConfigType: runtime?.config }),
				componentTourCompleted,
			],
			autoStartNext: false,
		},
		hubspotSaveAssociation: {
			start: 'task-definition',
			steps: (runtime?: { config?: MicroTaskConfigType | undefined }) => [
				getTaskDefinition(
					'The HubSpot Save Associations task creates associations for HubSpot objects.'
				),
				...destinations,
				...getHubspotSaveAssociationTourSteps({
					defaultStyles,
					microTaskConfigType: runtime?.config,
				}),
				componentTourCompleted,
			],
			autoStartNext: false,
		},
		hubspotSaveObjects: {
			start: 'task-definition',
			steps: (runtime?: { config?: MicroTaskConfigType | undefined }) => [
				getTaskDefinition(
					'Once a valid access token is given, the user chooses an object type from a dropdown and is then able to select which fields will be saved by either adding field mappings or using the auto map fields option. During execution, the task will use the input and the configured object type and fields to save the object(s) in Hubspot.'
				),
				...destinations,
				...getHubspotSaveObjectsTourSteps({
					defaultStyles,
					microTaskConfigType: runtime?.config,
				}),
				componentTourCompleted,
			],
			autoStartNext: false,
		},
		hubspotWebhook: {
			start: 'task-definition',
			steps: (runtime?: { config?: MicroTaskConfigType | undefined }) => [
				getTaskDefinition(
					'The HubSpot Webhook tasks enables iHub to receive REST API calls from HubSpot while supporting their proprietary security measures.'
				),
				...destinations,
				...getHubspotWebhookTourSteps({
					defaultStyles,
					microTaskConfigType: runtime?.config,
				}),
				componentTourCompleted,
			],
			autoStartNext: false,
		},
		ifGate: {
			start: 'task-definition',
			steps: (runtime?: { config?: MicroTaskConfigType | undefined }) => [
				getTaskDefinition(
					'The If Gate task evaluates the incoming data object and directs it to other tasks in the flow based on configured rules.'
				),
				...destinations,
				...getIfGateTourSteps({
					defaultStyles,
					microTaskConfigType: runtime?.config,
				}),
				componentTourCompleted,
			],
			autoStartNext: false,
		},
		mergeData: {
			start: 'task-definition',
			steps: (runtime?: { config?: MicroTaskConfigType | undefined }) => [
				getTaskDefinition(
					'This task is used to merge data into records in various ways and from different sources.'
				),
				...destinations,
				...getMergeDataTourSteps({
					defaultStyles,
					microTaskConfigType: runtime?.config,
				}),
				componentTourCompleted,
			],
			autoStartNext: false,
		},
		notification: {
			start: 'task-definition',
			steps: (runtime?: { config?: MicroTaskConfigType | undefined }) => [
				getTaskDefinition(
					'The notification task sends a notification to the configured notification channel(s).'
				),
				...destinations,
				...getNotificationTourSteps({
					defaultStyles,
					microTaskConfigType: runtime?.config,
				}),
				componentTourCompleted,
			],
			autoStartNext: false,
		},
		restApiSender: {
			start: 'task-definition',
			steps: (runtime?: { config?: MicroTaskConfigType | undefined }) => [
				getTaskDefinition(
					'The Rest API Sender Service allows the flow to send a REST request to a configurable endpoint.'
				),
				...destinations,
				...getRestApiSenderTourSteps({
					defaultStyles,
					microTaskConfigType: runtime?.config,
				}),
				componentTourCompleted,
			],
			autoStartNext: false,
		},
		salesforceSaveObjects: {
			start: 'task-definition',
			steps: (runtime?: { config?: MicroTaskConfigType | undefined }) => [
				getTaskDefinition(
					'The Salesforce Save Objects task allows the user to update or create any of the supported Salesforce objects.'
				),
				...destinations,
				...getSalesforceSaveObjectsTourSteps({
					defaultStyles,
					microTaskConfigType: runtime?.config,
				}),
				componentTourCompleted,
			],
			autoStartNext: false,
		},
		scheduleTask: {
			start: 'task-definition',
			steps: (runtime?: { config?: MicroTaskConfigType | undefined }) => [
				getTaskDefinition('The Schedule task is a time based trigger for flows and integrations.'),
				...destinations,
				...getScheduleTaskTourSteps({
					defaultStyles,
					microTaskConfigType: runtime?.config,
				}),
				componentTourCompleted,
			],
			autoStartNext: false,
		},
		scriptBlock: {
			start: 'task-definition',
			steps: (runtime?: { config?: MicroTaskConfigType | undefined }) => [
				getTaskDefinition(
					'Note: The script block is TEMPORARY. It will be removed once the product goes to market.'
				),
				...destinations,
				...getScriptBlockTourSteps({
					defaultStyles,
					microTaskConfigType: runtime?.config,
				}),
				componentTourCompleted,
			],
			autoStartNext: false,
		},
		sftpReader: {
			start: 'task-definition',
			steps: (runtime?: { config?: MicroTaskConfigType | undefined }) => [
				getTaskDefinition(
					'The SFTP Reader task provides the ability for iHub to connect to an SFTP server and then read files from that server.'
				),
				...destinations,
				...getSFTPReaderTourSteps({
					defaultStyles,
					microTaskConfigType: runtime?.config,
				}),
				componentTourCompleted,
			],
			autoStartNext: false,
		},
		sftpWriter: {
			start: 'task-definition',
			steps: (runtime?: { config?: MicroTaskConfigType | undefined }) => [
				getTaskDefinition(
					'The SFTP Writer provides the ability for iHub to connect to a SFTP server and write files to the server.'
				),
				...destinations,
				...getSFTPWriterTourSteps({
					defaultStyles,
					microTaskConfigType: runtime?.config,
				}),
				componentTourCompleted,
			],
			autoStartNext: false,
		},
		takeFromArray: {
			start: 'task-definition',
			steps: (runtime?: { config?: MicroTaskConfigType | undefined }) => [
				getTaskDefinition(
					'The take from array task is used to extract the specified item from a selected collection of items.'
				),
				...destinations,
				...getTakeFromArrayTourSteps({
					defaultStyles,
					microTaskConfigType: runtime?.config,
				}),
				componentTourCompleted,
			],
			autoStartNext: false,
		},
		validation: {
			start: 'task-definition',
			steps: (runtime?: { config?: MicroTaskConfigType | undefined }) => [
				getTaskDefinition(
					'The validation task takes the input data and then runs it against a set of user configured rules to determine if the records are valid or not. The data will then be routed accordingly.'
				),
				...destinations,
				...getValidationTaskTourSteps({
					defaultStyles,
					microTaskConfigType: runtime?.config,
				}),
				componentTourCompleted,
			],
			autoStartNext: false,
		},
		xeroCreateInvoice: {
			start: 'task-definition',
			steps: (runtime?: { config?: MicroTaskConfigType | undefined }) => [
				getTaskDefinition(
					'The Xero Create Invoice task allows the user to create an invoice in Xero based on the input data and the configured field mappings.'
				),
				...destinations,
				...getXeroCreateInvoiceTaskTourSteps({
					defaultStyles,
					microTaskConfigType: runtime?.config,
				}),
				componentTourCompleted,
			],
			autoStartNext: false,
		},
		addItem: {
			start: 'task-definition',
			steps: (runtime?: { config?: MicroTaskConfigType | undefined }) => [
				getTaskDefinition(
					'Builds a new JSON object from static/expression field mappings and adds it to processingData - as the whole record, nested under a field, or pushed into an existing array.'
				),
				...destinations,
				...getAddItemTaskTourSteps({
					defaultStyles,
					microTaskConfigType: runtime?.config,
				}),
				componentTourCompleted,
			],
			autoStartNext: false,
		},
	};

	return MicroTaskRegistry;
}
